/**
 * Portfolio drill-down (ARCHITECTURE.md §8). The dashboard's charts at more depth
 * — value over time, the profit-vs-new-money decomposition, and allocation — plus
 * the two things unique to this page: the live **holdings table** and the
 * **cashflow event history** (the decomposition's substrate). Live holdings are
 * `trading`-gated (§7); when the Toss credential isn't verified yet the panel
 * renders a data-driven CTA rather than an error. Period is a typed `?period=`
 * search param; all server state is TanStack Query over the typed client.
 */
import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@richrich/i18n/react";
import type { Locale } from "@richrich/i18n";
import {
  AllocationDonut,
  Card,
  DecompositionPanel,
  PortfolioValueChart,
  Stat,
  decimalSign,
  directionFromSign,
  formatDateTime,
  formatMoney,
} from "@richrich/ui";
import { type PortfolioPeriod, type PortfolioSummaryResponse } from "@richrich/contracts";
import { settingsQuery } from "../lib/query";
import {
  DEFAULT_BASE_CURRENCY,
  DEFAULT_PERIOD,
  allocationQuery,
  cashflowsQuery,
  decompositionQuery,
  emptyAllocation,
  emptyDecomposition,
  emptyTimeseries,
  holdingsQuery,
  parsePeriodSearch,
  periodToRange,
  summaryQuery,
  timeseriesQuery,
} from "../features/dashboard/api";
import { useTriggerSnapshot } from "../features/dashboard/hooks";
import { ErrorPanel, GatePanel } from "../features/dashboard/panels";
import { HoldingsTable } from "../features/dashboard/HoldingsTable";
import { CashflowPanel } from "../features/dashboard/CashflowPanel";
import { Button } from "../components/ui/controls";

export const Route = createFileRoute("/portfolio")({
  validateSearch: (search: Record<string, unknown>): { period?: PortfolioPeriod } =>
    parsePeriodSearch(search.period),
  loaderDeps: ({ search }) => ({ period: search.period ?? DEFAULT_PERIOD }),
  loader: ({ context, deps }) => {
    const { queryClient } = context;
    const { from, to } = periodToRange(deps.period);
    void queryClient.prefetchQuery(timeseriesQuery(deps.period));
    void queryClient.prefetchQuery(decompositionQuery(from, to));
    void queryClient.prefetchQuery(allocationQuery);
    void queryClient.prefetchQuery(summaryQuery);
    void queryClient.prefetchQuery(cashflowsQuery);
  },
  component: PortfolioPage,
});

function PortfolioPage() {
  const { t, locale } = useI18n();
  const search = Route.useSearch();
  const period = search.period ?? DEFAULT_PERIOD;
  const navigate = useNavigate({ from: Route.fullPath });
  const setPeriod = (next: PortfolioPeriod) =>
    void navigate({ search: (prev) => ({ ...prev, period: next }) });

  const { from, to } = useMemo(() => periodToRange(period), [period]);

  const settings = useQuery(settingsQuery);
  const baseCurrency = settings.data?.baseCurrency ?? DEFAULT_BASE_CURRENCY;
  const tossUnlocked = settings.data?.features.toss === true;

  const summary = useQuery(summaryQuery);
  const timeseries = useQuery(timeseriesQuery(period));
  const decomposition = useQuery(decompositionQuery(from, to));
  const allocation = useQuery(allocationQuery);
  const cashflows = useQuery(cashflowsQuery);
  const holdings = useQuery({ ...holdingsQuery, enabled: tossUnlocked });

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-8 py-7">
      <header className="flex items-start justify-between gap-4 border-b border-hairline pb-5">
        <div>
          <div className="mb-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-muted">
            {t("portfolio.accountsEyebrow")}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{t("nav.portfolio")}</h1>
          <p className="mt-1.5 max-w-2xl text-sm text-ink-secondary">
            {t("portfolio.description")}
          </p>
        </div>
        <SnapshotNowButton />
      </header>

      <SummaryStrip summary={summary.data ?? null} loading={summary.isPending} locale={locale} />

      {timeseries.isError ? (
        <ErrorPanel
          title={t("dashboard.panelPortfolioValue")}
          error={timeseries.error}
          onRetry={() => timeseries.refetch()}
        />
      ) : (
        <PortfolioValueChart
          data={timeseries.data ?? emptyTimeseries(period, baseCurrency)}
          period={period}
          onPeriodChange={setPeriod}
          loading={timeseries.isPending}
          height={300}
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {decomposition.isError ? (
            <ErrorPanel
              title={t("dashboard.panelDecomposition")}
              error={decomposition.error}
              onRetry={() => decomposition.refetch()}
              height={340}
            />
          ) : (
            <DecompositionPanel
              data={decomposition.data ?? emptyDecomposition(from, to, baseCurrency)}
              loading={decomposition.isPending}
            />
          )}
        </div>
        <div>
          {allocation.isError ? (
            <ErrorPanel
              title={t("dashboard.panelAllocation")}
              error={allocation.error}
              onRetry={() => allocation.refetch()}
              height={260}
            />
          ) : (
            <AllocationDonut
              data={allocation.data ?? emptyAllocation(baseCurrency)}
              loading={allocation.isPending}
            />
          )}
        </div>
      </div>

      {!tossUnlocked ? (
        <GatePanel
          title={t("portfolio.holdingsTitle")}
          message={t("portfolio.holdingsGateMessage")}
          cta={{ label: t("portfolio.openSettings"), to: "/settings" }}
        />
      ) : holdings.isError ? (
        <ErrorPanel
          title={t("portfolio.holdingsTitle")}
          error={holdings.error}
          onRetry={() => holdings.refetch()}
        />
      ) : (
        <HoldingsTable data={holdings.data ?? null} loading={holdings.isPending} />
      )}

      {cashflows.isError ? (
        <ErrorPanel
          title={t("portfolio.cashflowTitle")}
          error={cashflows.error}
          onRetry={() => cashflows.refetch()}
        />
      ) : (
        <CashflowPanel
          data={cashflows.data}
          loading={cashflows.isPending}
          baseCurrency={baseCurrency}
        />
      )}
    </div>
  );
}

// ── manual snapshot trigger ──────────────────────────────────────────

function SnapshotNowButton() {
  const { t } = useI18n();
  const [queued, setQueued] = useState(false);

  const snapshot = useTriggerSnapshot();

  const runSnapshot = (): void => {
    snapshot.mutate(undefined, {
      onSuccess: () => {
        setQueued(true);
        // Snapshots settle async on the queue side — wait a beat (matching the
        // hook's own delayed invalidation) before clearing the indicator.
        window.setTimeout(() => setQueued(false), 3000);
      },
    });
  };

  return (
    <div className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
      <Button variant="secondary" size="sm" pending={snapshot.isPending} onClick={runSnapshot}>
        {t("portfolio.snapshotNow")}
      </Button>
      {queued && <span className="text-xs text-muted">{t("portfolio.snapshotQueued")}</span>}
      {snapshot.isError && (
        <span className="text-xs text-critical">{t("common.errorGeneric")}</span>
      )}
    </div>
  );
}

// ── summary strip ──────────────────────────────────────────────────

function SummaryStrip({
  summary,
  loading,
  locale,
}: {
  summary: PortfolioSummaryResponse | null;
  loading: boolean;
  locale: Locale;
}) {
  const { t } = useI18n();
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i}>
            <div className="h-3 w-20 animate-pulse rounded bg-muted/20 motion-reduce:animate-none" />
            <div className="mt-3 h-6 w-28 animate-pulse rounded bg-muted/20 motion-reduce:animate-none" />
          </Card>
        ))}
      </div>
    );
  }

  if (summary === null) {
    return (
      <Card>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-ink">{t("portfolio.noSnapshotTitle")}</div>
            <p className="mt-1 text-xs text-muted">{t("portfolio.noSnapshotBody")}</p>
          </div>
        </div>
      </Card>
    );
  }

  const cur = summary.baseCurrency;
  const pnlSign = decimalSign(summary.cumMarketPnl);

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <Card>
        <Stat
          label={t("portfolio.totalValue")}
          size="lg"
          value={formatMoney(summary.totalValue, cur)}
          caption={t("portfolio.asOf", { date: formatDateTime(summary.takenAt, locale) })}
        />
      </Card>
      <Card>
        <Stat label={t("portfolio.stocks")} value={formatMoney(summary.stocksValue, cur)} />
      </Card>
      <Card>
        <Stat label={t("portfolio.cash")} value={formatMoney(summary.cashValue, cur)} />
      </Card>
      <Card>
        <Stat
          label={t("portfolio.netDeposits")}
          value={formatMoney(summary.cumExternal, cur, { signed: true })}
          delta={{
            text: formatMoney(summary.cumMarketPnl, cur, { signed: true }),
            direction: directionFromSign(pnlSign),
            hint: t("portfolio.marketPnlHint"),
          }}
        />
      </Card>
    </div>
  );
}
